import PouchDB from "pouchdb";
import PouchDBUpsert from "pouchdb-upsert";
import PouchDBFind from "pouchdb-find";
import { type HunterParkCsvRow } from "@/types";
import { potaParksLogger } from "@/utils/loggers";

// User-specific store of parks marked "hunted" via an imported pota.app
// hunter_parks.csv. This is deliberately SEPARATE from pota-parks.db, which holds
// global reference data (all parks) and is overwritten by the daily API sync.
PouchDB.plugin(PouchDBUpsert);
PouchDB.plugin(PouchDBFind);

const hunterParkDb = new PouchDB("hunter-parks.db");
const METADATA_DOC_ID = "HUNTER_PARKS_METADATA";

export type HunterParkDbMetaData = {
  _id: string;
  lastImportedAt: number; // ms epoch of the last successful import, 0 if never
  rowCount: number;
};

type HunterParkDoc = HunterParkCsvRow & { _id: string };

function toDbRow(row: HunterParkCsvRow): HunterParkDoc {
  return { ...row, _id: row.reference };
}

// Serialize mutating operations (replaceAll / clearAll) on the shared db. Each yields
// repeatedly (setTimeout(0) between batches), so without this an overlapping import + clear
// could interleave and prune docs the other just wrote, leaving a set matching neither.
let writeLock: Promise<unknown> = Promise.resolve();
function serializeWrite<T>(fn: () => Promise<T>): Promise<T> {
  const run = writeLock.then(fn, fn);
  // Keep the chain alive regardless of success/failure so one rejection can't wedge it.
  writeLock = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

const HunterParkPouchRepo = {
  async getMetadata(): Promise<HunterParkDbMetaData> {
    try {
      return await hunterParkDb.get<HunterParkDbMetaData>(METADATA_DOC_ID);
    } catch (error: any) {
      if (error.status === 404) {
        return { _id: METADATA_DOC_ID, lastImportedAt: 0, rowCount: 0 };
      }
      throw error;
    }
  },

  async updateMetadata(updates: Partial<HunterParkDbMetaData>): Promise<void> {
    await hunterParkDb.upsert(METADATA_DOC_ID, (doc: any) => ({
      _id: METADATA_DOC_ID,
      lastImportedAt: 0,
      rowCount: 0,
      ...doc,
      ...updates,
    }));
  },

  async findAll(): Promise<HunterParkCsvRow[]> {
    const result = await hunterParkDb.allDocs<HunterParkDoc>({ include_docs: true });
    return result.rows
      .filter((row) => row.id !== METADATA_DOC_ID && row.doc)
      .map((row) => row.doc as HunterParkCsvRow);
  },

  async getCount(): Promise<number> {
    const result = await hunterParkDb.allDocs();
    return result.rows.filter((row) => row.id !== METADATA_DOC_ID).length;
  },

  // Delete every park doc (keeping the metadata doc).
  async clearParks(): Promise<void> {
    const result = await hunterParkDb.allDocs();
    const toDelete = result.rows
      .filter((row) => row.id !== METADATA_DOC_ID)
      .map((row) => ({ _id: row.id, _rev: row.value.rev, _deleted: true }));
    if (toDelete.length) {
      await hunterParkDb.bulkDocs(toDelete as any);
    }
  },

  // Replace the entire imported set with `rows` (a fresh export is authoritative).
  // Order matters for crash-safety: write the new rows first, then prune the previous
  // import's leftovers, then advance metadata last. A failure mid-write therefore leaves
  // the prior import intact (possibly with new rows layered on) rather than wiped, and
  // metadata only moves forward on full success.
  async replaceAll(rows: HunterParkCsvRow[]): Promise<void> {
    return serializeWrite(async () => {
      const keep = new Set<string>();
      const batchSize = 50;
      for (let i = 0; i < rows.length; i += batchSize) {
        const batch = rows.slice(i, i + batchSize);
        await Promise.all(
          batch.map((row) => {
            keep.add(row.reference);
            return hunterParkDb.upsert(row.reference, () => toDbRow(row));
          })
        );
        // Yield so a large import doesn't block the UI.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // Prune docs from the previous import that aren't in the new set.
      const existing = await hunterParkDb.allDocs();
      const toDelete = existing.rows
        .filter((row) => row.id !== METADATA_DOC_ID && !keep.has(row.id))
        .map((row) => ({ _id: row.id, _rev: row.value.rev, _deleted: true }));
      if (toDelete.length) {
        await hunterParkDb.bulkDocs(toDelete as any);
      }

      await this.updateMetadata({ lastImportedAt: Date.now(), rowCount: rows.length });
      potaParksLogger(`Imported ${rows.length} hunter parks`);
    });
  },

  // Remove all imported parks and reset metadata (user "clear" action).
  async clearAll(): Promise<void> {
    return serializeWrite(async () => {
      await this.clearParks();
      await this.updateMetadata({ lastImportedAt: 0, rowCount: 0 });
      potaParksLogger("Cleared imported hunter parks");
    });
  },
};

export default HunterParkPouchRepo;
